import Col from "components/grid/col";
import Grid from "components/grid/grid";
import Row from "components/grid/row";
import Block from "components/layout/block";
import { Fragment, FunctionComponent, useState } from "react";
import BaseStatus from "./base-status";
import Button from "components/button";
import { useListUser } from "stores/hooks/list-user";
import {
  adminDoDeleteListUser,
  adminDoPatchListUser,
  adminDoPatchListUserStatusApprove,
  adminDoPatchListUserStatusReject,
  adminDoPatchListUserStatusSubmit,
  adminDoPostListUserGenerate,
} from "services/admin/list-user";
import { fetchAdminListUser } from "stores/actions/list-user";
import { useList } from "stores/hooks/list";
import { showError, showGenericError, showSuccess } from "lib/notification";
import { LocaleString } from "models/locale";
import { adminDoPostListUserInvitation } from "services/admin/list-invitation";
import { adminDoPostListInvitationEmail } from "services/admin/list-invitation-email";
import ApproveModal from "../modals/approve-modal";
import MessageModal from "../modals/message-modal";
import {
  faBan,
  faBolt,
  faBoxArchive,
  faPaperPlane,
  faStamp,
  faTrash,
  faTruck,
} from "components/icon-library";
import RejectModal from "../modals/reject-modal";
import SubmitModal from "../modals/submit-modal";
import DeleteModal from "../modals/delete-modal";
import ArchiveModal from "../modals/archive-modal";
import { useNavigate } from "react-router";

interface StatusBlockProps {
  onUpdate: () => Promise<void>;
}

const StatusBlock: FunctionComponent<StatusBlockProps> = ({ onUpdate }) => {
  const list = useList();
  const listUser = useListUser();
  const [approveModal, toggleApproveModal] = useState(false);
  const [submitModal, toggleSubmitModal] = useState(false);
  const [rejectModal, toggleRejectModal] = useState(false);
  const [messageModal, toggleMessageModal] = useState(false);
  const [archiveModel, toggleArchiveModal] = useState(false);
  const [deleteModal, toggleDeleteModal] = useState(false);
  const navigate = useNavigate();

  if (!list || !listUser) {
    return null;
  }

  const { status } = listUser;

  const handleReject = async (
    send_email: boolean,
    subject: string,
    message: string,
  ) => {
    try {
      await adminDoPatchListUserStatusReject(
        list,
        listUser.uuid,
        send_email,
        subject,
        message,
      );
      await fetchAdminListUser(list, listUser.uuid);
      showSuccess("Ilmoitus palautettu korjattavaksi.");
    } catch (err) {
      showError("Ilmoituksen palauttaminen korjattavaksi epäonnistui.");
    }
  };

  const handleSubmit = async () => {
    try {
      await adminDoPatchListUserStatusSubmit(list, listUser.uuid);

      await fetchAdminListUser(list, listUser.uuid);

      showSuccess("Ilmoitus lähetetty tarkastettavaksi.");
    } catch (err) {
      showError("Ilmoituksen lähettäminen tarkastettavaksi epäonnistui.");
    }
  };

  const handleApprove = async (
    approved_at: Date,
    send_email: boolean,
    subject: LocaleString,
    message: LocaleString,
  ) => {
    try {
      await adminDoPatchListUserStatusApprove(
        list,
        listUser.uuid,
        approved_at.toISOString(),
        send_email,
        subject,
        message,
      );
      await fetchAdminListUser(list, listUser.uuid);

      showSuccess("Ilmoitus tarkastettu.");
    } catch (err) {
      showError("Ilmoituksen tarkastaminen epäonnistui.");
    }
  };

  const handleDelete = async () => {
    try {
      await adminDoDeleteListUser(list, listUser.uuid);
      navigate(`/admin/list/${list.uuid}`);
      showSuccess("Ilmoitusvelvollinen poistettu.");
    } catch (err) {
      showGenericError();
    }
  };

  const handleArchive = async (archived_at: any) => {
    try {
      await adminDoPatchListUser(list, listUser.uuid, {
        archived: true,
        archived_at,
      });
      navigate(`/admin/list/${list.uuid}`);
      showSuccess("Ilmoitusvelvollisen tehtävä merkitty päättyneeksi");
    } catch (err) {
      showGenericError();
    }
  };

  const handleMessage = async (subject: string, message: string) => {
    try {
      // craete invitation
      const invitation = await adminDoPostListUserInvitation(
        list,
        listUser.uuid,
      );
      await adminDoPostListInvitationEmail(
        list,
        invitation.uuid,
        subject,
        message,
      );

      // the message is already sent, just update the state
      await fetchAdminListUser(list, listUser.uuid);

      // notify
      showSuccess("Viesti lähetetty.");
    } catch (err) {
      showError("Viestin lähettäminen epäonnistui.");
    }
  };

  const generateContent = async () => {
    await adminDoPostListUserGenerate(list, listUser.uuid);
    await fetchAdminListUser(list, listUser.uuid);
    await onUpdate();
  };

  return (
    <Fragment>
      <Block title="Ilmoituksen tila">
        <Grid>
          <Row>
            <Col>
              <BaseStatus />
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                type="primary"
                disabled={status === "draft"}
                preIcon={faBan}
                onClick={async () => toggleRejectModal(true)}
              >
                Palauta korjattavaksi
              </Button>
              <Button
                type="primary"
                disabled={status === "approved" || status === "submitted"}
                preIcon={faTruck}
                onClick={async () => toggleSubmitModal(true)}
              >
                Lähetä tarkastettavaksi
              </Button>
              <Button
                type="primary"
                disabled={status === "draft" || status === "approved"}
                preIcon={faStamp}
                onClick={async () => toggleApproveModal(true)}
              >
                Tarkasta ilmoitus
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                type="secondary"
                preIcon={faPaperPlane}
                onClick={async () => toggleMessageModal(true)}
              >
                Lähetä viesti
              </Button>
              <Button
                type="secondary"
                preIcon={faBolt}
                onClick={generateContent}
              >
                Luo testisisältöä
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                type="secondary"
                preIcon={faBoxArchive}
                onClick={async () => toggleArchiveModal(true)}
              >
                Päätä tehtävä
              </Button>
              <Button
                type="secondary"
                preIcon={faTrash}
                onClick={async () => toggleDeleteModal(true)}
              >
                Poista ilmoitusvelvollinen
              </Button>
            </Col>
          </Row>
        </Grid>
      </Block>
      {approveModal && (
        <ApproveModal
          onSubmit={handleApprove}
          onClose={async () => toggleApproveModal(false)}
        />
      )}
      {submitModal && (
        <SubmitModal
          onSubmit={handleSubmit}
          onClose={async () => toggleSubmitModal(false)}
        />
      )}
      {rejectModal && (
        <RejectModal
          onSubmit={handleReject}
          onClose={async () => toggleRejectModal(false)}
        />
      )}
      {messageModal && (
        <MessageModal
          onSubmit={handleMessage}
          onClose={async () => toggleMessageModal(false)}
        />
      )}
      {archiveModel && (
        <ArchiveModal
          onSubmit={handleArchive}
          onClose={async () => toggleArchiveModal(false)}
        />
      )}
      {deleteModal && (
        <DeleteModal
          onSubmit={handleDelete}
          onClose={async () => toggleDeleteModal(false)}
        />
      )}
    </Fragment>
  );
};

export default StatusBlock;
